import React, { useState } from "react";
import {
  IoAlertCircleOutline,
  IoCheckmark,
  IoCheckmarkCircle,
  IoCopyOutline,
  IoDownloadOutline,
  IoEllipse,
  IoEyeOutline,
  IoInformationCircleOutline,
  IoKeyOutline,
  IoServerOutline,
  IoSparklesOutline,
  IoSyncOutline,
  IoTimeOutline,
  IoTrashOutline,
  IoWarningOutline,
} from "react-icons/io5";
import Wordmark from "../../components/common/Wordmark";

/*
 * ─────────────────────────────────────────────────────────────────────────
 *  API Keys — design preview (hidden route: /preview/api-keys)
 *
 *  Fully-faked UX prototype. No backend calls. Intended as a working doc
 *  for eng to reverse-engineer the next-gen API contract from.
 *
 *  Core model (per Alexey's note of 4 June 2026):
 *    - One bucket per USER (Storage exports)
 *    - One BigQuery dataset per USER, with an authorized view into the
 *      central realtime stream, filtered to that user's data
 *    - Many KEYS per user → many service accounts, each granted access
 *      to the user-level resources. Row-level filter on api_key_id in
 *      the view keeps per-key tenancy honest.
 *
 *  So: user-level resources are surfaced once (in "Your data"); per-key
 *  resources are only the service account and IAM grants.
 * ─────────────────────────────────────────────────────────────────────────
 */

const AlertIcon = IoAlertCircleOutline as any;
const CheckIcon = IoCheckmark as any;
const CheckCircleIcon = IoCheckmarkCircle as any;
const CopyIcon = IoCopyOutline as any;
const DownloadIcon = IoDownloadOutline as any;
const DotIcon = IoEllipse as any;
const EyeIcon = IoEyeOutline as any;
const InfoIcon = IoInformationCircleOutline as any;
const KeyIcon = IoKeyOutline as any;
const ServerIcon = IoServerOutline as any;
const SparklesIcon = IoSparklesOutline as any;
const SyncIcon = IoSyncOutline as any;
const TimeIcon = IoTimeOutline as any;
const TrashIcon = IoTrashOutline as any;
const WarningIcon = IoWarningOutline as any;

// ─── Domain model the mock proposes ─────────────────────────────────────

type ScopeId = "exports.read" | "realtime.read" | "aggregates.read";

interface ScopeDef {
  id: ScopeId;
  title: string;
  shortTitle: string;
  description: string;
  /** Whether this scope needs the user-level BigQuery dataset/view. */
  needsBigQuery?: boolean;
  comingSoon?: boolean;
}

const USER_HANDLE = "usr_8mhyi"; // fake — would come from auth

const SCOPES: ScopeDef[] = [
  {
    id: "exports.read",
    title: "Read aggregate exports",
    shortTitle: "Exports",
    description:
      "Download daily aggregate dumps from your personal results bucket. Pick this if you only consume exports.",
  },
  {
    id: "realtime.read",
    title: "Query realtime data",
    shortTitle: "Realtime",
    description:
      "Run BigQuery queries against your personal authorized view into the centralized realtime dataset, filtered to your data only.",
    needsBigQuery: true,
  },
  {
    id: "aggregates.read",
    title: "Query aggregate datasets",
    shortTitle: "Aggregates",
    description:
      "Same authorized-view pattern as Realtime, but for aggregate data. Useful for partners who want tabular access instead of file dumps.",
    needsBigQuery: true,
    comingSoon: true,
  },
];

const SCOPE_BY_ID: Record<ScopeId, ScopeDef> = SCOPES.reduce(
  (acc, s) => ({ ...acc, [s.id]: s }),
  {} as Record<ScopeId, ScopeDef>
);

// User-level resources — one per user, shared by all their keys.
const USER_RESOURCES = {
  bucketPath: `gs://telcofy-user-data/results/${USER_HANDLE}/`,
  datasetId: `telcofy-prod-data:user_${USER_HANDLE}`,
  realtimeView: "realtime_data",
  // (future) aggregates view → "aggregates_data"
};

function normalizeLabel(label: string) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

interface MockKey {
  id: string;
  label: string;
  scopes: ScopeId[];
  serviceAccountEmail: string;
  createdAt: string;
  lastUsedAt: string | null;
  /** Present only in the one-time reveal payload. */
  apiKey?: string;
}

function makeMockKey(label: string, scopes: ScopeId[]): MockKey {
  const slug = normalizeLabel(label) || "key";
  return {
    id: `mk_${Math.random().toString(36).slice(2, 10)}`,
    label,
    scopes,
    serviceAccountEmail: `${USER_HANDLE}-${slug}@telcofy-prod.iam.gserviceaccount.com`,
    createdAt: new Date().toISOString(),
    lastUsedAt: null,
    apiKey: `tcf_${[...crypto.getRandomValues(new Uint8Array(24))]
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")}`,
  };
}

// The example keys are dated relative to now, so the page never shows a made-up calendar date.
const DAY_MS = 24 * 60 * 60 * 1000;
const ago = (days: number, hours = 0) => new Date(Date.now() - days * DAY_MS - hours * 60 * 60 * 1000).toISOString();

// ─── Seed: storage-only keys so the user lands on a state where the
// user-level BigQuery dataset hasn't been provisioned yet. Creating the
// first BQ-scoped key triggers that one-time provisioning.

const SEED_KEYS: MockKey[] = [
  {
    id: "mk_seed1",
    label: "etl-prod",
    scopes: ["exports.read"],
    serviceAccountEmail: `${USER_HANDLE}-etl-prod@telcofy-prod.iam.gserviceaccount.com`,
    createdAt: ago(40),
    lastUsedAt: ago(2, 3),
  },
  {
    id: "mk_seed2",
    label: "ramboll-export",
    scopes: ["exports.read"],
    serviceAccountEmail: `${USER_HANDLE}-ramboll-export@telcofy-prod.iam.gserviceaccount.com`,
    createdAt: ago(21),
    lastUsedAt: ago(0, 5),
  },
];

// ─── Visual primitives ──────────────────────────────────────────────────

const ScopePill: React.FC<{ scope: ScopeId }> = ({ scope }) => {
  const palette: Record<ScopeId, string> = {
    "exports.read": "border-paper-300 bg-paper-100 text-ink-soft",
    "realtime.read": "border-note-line bg-note-soft text-note-ink",
    "aggregates.read": "border-paper-300 bg-paper-200 text-ink-muted",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-mono font-semibold uppercase tracking-wider ${palette[scope]}`}
    >
      {SCOPE_BY_ID[scope].shortTitle}
    </span>
  );
};

// ─── Main page ──────────────────────────────────────────────────────────

const ApiKeysPreviewPage: React.FC = () => {
  const [keys, setKeys] = useState<MockKey[]>(SEED_KEYS);
  const [label, setLabel] = useState("");
  const [selectedScopes, setSelectedScopes] = useState<ScopeId[]>([]);
  const [revealed, setRevealed] = useState<MockKey | null>(null);
  const [revoking, setRevoking] = useState<MockKey | null>(null);
  const [rotating, setRotating] = useState<MockKey | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Derived: is the user-level BigQuery dataset provisioned?
  // Equivalent on the backend: "does the user have any key with a BQ scope?"
  const datasetProvisioned = keys.some((k) =>
    k.scopes.some((s) => SCOPE_BY_ID[s].needsBigQuery)
  );

  const trimmedLabel = label.trim();
  const labelTaken = keys.some(
    (k) => k.label.toLowerCase() === trimmedLabel.toLowerCase()
  );
  const canSubmit =
    !!trimmedLabel && !labelTaken && selectedScopes.length > 0;

  const willProvisionDataset =
    !datasetProvisioned &&
    selectedScopes.some((s) => SCOPE_BY_ID[s].needsBigQuery);

  const toggleScope = (id: ScopeId) => {
    setSelectedScopes((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleCreate = () => {
    setError(null);
    if (!trimmedLabel) {
      setError("Give the key a label so you can recognise it later.");
      return;
    }
    if (labelTaken) {
      setError(
        `A key called "${trimmedLabel}" already exists. Rotate it instead, or pick a new label.`
      );
      return;
    }
    if (selectedScopes.length === 0) {
      setError("Pick at least one permission so the key has something it can do.");
      return;
    }
    const created = makeMockKey(trimmedLabel, selectedScopes);
    setKeys((prev) => [created, ...prev]);
    setRevealed(created);
    setLabel("");
    setSelectedScopes([]);
  };

  const handleRotateConfirm = () => {
    if (!rotating) return;
    const rotated = makeMockKey(rotating.label, rotating.scopes);
    rotated.serviceAccountEmail = rotating.serviceAccountEmail;
    setKeys((prev) =>
      prev.map((k) => (k.id === rotating.id ? { ...rotated, id: rotating.id } : k))
    );
    setRevealed({ ...rotated, id: rotating.id });
    setRotating(null);
  };

  const handleRevokeConfirm = () => {
    if (!revoking) return;
    setKeys((prev) => prev.filter((k) => k.id !== revoking.id));
    setRevoking(null);
  };

  const previewSa = `${USER_HANDLE}-${normalizeLabel(trimmedLabel) || "your-key-label"}@telcofy-prod.iam.gserviceaccount.com`;
  const selectedScopeDefs = selectedScopes.map((s) => SCOPE_BY_ID[s]);

  return (
    <div className="min-h-screen bg-paper-200">
      {/* Minimal chrome — this page is intentionally unlinked. */}
      <header className="border-b border-paper-300/70 bg-surface">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <Wordmark className="text-2xl" />
          <span className="rounded-full bg-warn-soft border border-warn-line px-3 py-1 text-[11px] font-mono font-semibold uppercase tracking-widest text-warn-ink">
            Design preview · not connected to APIs
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10">
        {/* Page intro */}
        <div className="mb-8">
          <p className="text-xs font-mono uppercase tracking-widest text-ink-faint mb-3">
            Developer
          </p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-ink mb-3">
            API keys
          </h1>
          <p className="text-lg text-ink-muted">
            Programmatic access to your Telcofy data. Each key has a set of
            permissions you pick, and only those.
          </p>
        </div>

        {/* What this prototype is asking the backend to support */}
        <div className="mb-8 flex items-start gap-3 rounded-2xl border border-note-line bg-note-soft/60 p-4 text-sm text-note-ink">
          <InfoIcon className="h-5 w-5 flex-shrink-0 mt-0.5 text-note-ink" />
          <div>
            <p className="font-semibold mb-1">
              What this prototype is asking the backend to support
            </p>
            <ul className="list-disc pl-5 space-y-0.5 text-note-ink/90">
              <li>
                <strong>One bucket + one BigQuery dataset per user</strong>,
                provisioned lazily. Many keys share these — each key's service
                account is granted access; the view's row-level filter on{" "}
                <code className="font-mono text-xs">api_key_id</code> keeps
                per-key tenancy honest. Datasets are <em>never</em> per-key.
              </li>
              <li>
                One key can hold <strong>multiple permissions</strong>{" "}
                (Exports + Realtime + future Aggregates) — picked as
                checkboxes, not a single &quot;service&quot; radio.
              </li>
              <li>
                The user <strong>names the key</strong>, never the dataset.
                Bucket + dataset + view names are derived from the user, shown
                in &quot;Your data&quot; (below) so users can paste them into
                their queries.
              </li>
              <li>
                <strong>Side effects are visible up-front</strong> — before
                hitting Create, the user sees the service account and any
                first-time dataset provisioning.
              </li>
              <li>
                <strong>Rotation</strong> keeps the same SA + resources, only
                the key string changes. <strong>Revoke</strong> is a separate
                terminal action.
              </li>
            </ul>
          </div>
        </div>

        {/* Your data — user-level resources, surfaced once */}
        <section className="mb-8 rounded-3xl border border-paper-300/70 bg-surface p-6 shadow-soft">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-display text-lg font-bold text-ink">
                Your data
              </h2>
              <p className="text-xs text-ink-muted mt-0.5">
                Provisioned for your account. All your keys read from these
                resources — paste the names into your queries.
              </p>
            </div>
            <span className="text-[11px] font-mono uppercase tracking-widest text-ink-faint">
              Account · <span className="text-ink-soft">{USER_HANDLE}</span>
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Storage */}
            <div className="rounded-2xl border border-paper-300/70 bg-paper-100/60 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-ink-soft">
                  Aggregate exports
                </span>
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-brand-700">
                  <CheckCircleIcon className="h-3 w-3" />
                  Ready
                </span>
              </div>
              <div className="text-[11px] text-ink-faint mb-1">Cloud Storage</div>
              <code className="block font-mono text-xs text-ink break-all">
                {USER_RESOURCES.bucketPath}
              </code>
            </div>

            {/* BigQuery */}
            <div className="rounded-2xl border border-paper-300/70 bg-paper-100/60 p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-ink-soft">
                  Realtime BigQuery view
                </span>
                {datasetProvisioned ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-brand-700">
                    <CheckCircleIcon className="h-3 w-3" />
                    Ready
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-ink-faint">
                    <TimeIcon className="h-3 w-3" />
                    Not yet provisioned
                  </span>
                )}
              </div>
              {datasetProvisioned ? (
                <>
                  <div className="text-[11px] text-ink-faint mb-1">
                    Dataset · view
                  </div>
                  <code className="block font-mono text-xs text-ink break-all">
                    {USER_RESOURCES.datasetId}.{USER_RESOURCES.realtimeView}
                  </code>
                </>
              ) : (
                <p className="text-xs text-ink-muted leading-relaxed">
                  Created automatically the first time you make a key with a
                  BigQuery permission.
                </p>
              )}
            </div>
          </div>
        </section>

        {/* Create form */}
        <section className="mb-8 rounded-3xl border border-paper-300/70 bg-surface p-6 shadow-soft">
          <h2 className="font-display text-xl font-bold text-ink mb-1">
            Create a new key
          </h2>
          <p className="text-sm text-ink-muted mb-5">
            Pick a label you'll recognise and choose what the key should be
            able to do.
          </p>

          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-bad-line bg-bad-soft px-4 py-3 text-sm text-bad-ink">
              <AlertIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-5">
            {/* Label */}
            <div>
              <label
                htmlFor="preview-label"
                className="block text-sm font-medium text-ink-soft mb-1.5"
              >
                Label
              </label>
              <input
                id="preview-label"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. analytics-pipeline, ramboll-export, my-laptop"
                className="w-full rounded-xl border border-paper-300 bg-surface px-4 py-2.5 text-sm placeholder:text-ink-faint focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 transition"
              />
              {labelTaken && (
                <p className="mt-2 text-xs text-warn-ink">
                  A key with this label already exists — use rotation to
                  refresh it instead.
                </p>
              )}
            </div>

            {/* Permissions */}
            <div>
              <span className="block text-sm font-medium text-ink-soft mb-2">
                Permissions
                <span className="font-normal text-ink-faint">
                  {" "}
                  · pick one or more
                </span>
              </span>
              <div className="grid grid-cols-1 gap-3">
                {SCOPES.map((scope) => {
                  const checked = selectedScopes.includes(scope.id);
                  const disabled = !!scope.comingSoon;
                  return (
                    <label
                      key={scope.id}
                      className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-4 transition ${
                        disabled
                          ? "border-paper-300 bg-paper-100/50 opacity-60 cursor-not-allowed"
                          : checked
                            ? "border-brand-500 bg-brand-50/40 ring-2 ring-brand-500/30"
                            : "border-paper-300 bg-surface hover:bg-paper-100"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleScope(scope.id)}
                        className="mt-0.5 h-4 w-4 rounded border-paper-400 text-brand-500 focus:ring-brand-500"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm text-ink">
                            {scope.title}
                          </span>
                          {scope.comingSoon && (
                            <span className="rounded-full bg-paper-200 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-ink-faint">
                              Coming soon
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-ink-muted leading-relaxed mt-1">
                          {scope.description}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* What will be created — per-key resources only.
                Includes a one-time "first BQ key" callout. */}
            {selectedScopes.length > 0 && (
              <div className="rounded-2xl border border-paper-300/70 bg-paper-100/60 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <ServerIcon className="h-4 w-4 text-ink-muted" />
                  <span className="text-xs font-mono uppercase tracking-widest text-ink-faint">
                    What we'll create
                  </span>
                </div>
                <ul className="space-y-2 text-xs">
                  <li className="flex items-start gap-2 text-ink-soft">
                    <DotIcon className="h-1.5 w-1.5 mt-1.5 flex-shrink-0 text-brand-500" />
                    <span>
                      <span className="text-ink-faint">
                        Google service account:
                      </span>{" "}
                      <span className="font-mono text-ink break-all">
                        {previewSa}
                      </span>
                    </span>
                  </li>
                  <li className="flex items-start gap-2 text-ink-soft">
                    <DotIcon className="h-1.5 w-1.5 mt-1.5 flex-shrink-0 text-brand-500" />
                    <span>
                      <span className="text-ink-faint">IAM grants:</span> read
                      access to your{" "}
                      {selectedScopeDefs.map((s, i) => (
                        <React.Fragment key={s.id}>
                          {i > 0 && (i === selectedScopeDefs.length - 1 ? " and " : ", ")}
                          <span className="font-mono text-ink">{s.shortTitle}</span>
                        </React.Fragment>
                      ))}
                      {" "}resources
                    </span>
                  </li>
                </ul>

                {willProvisionDataset && (
                  <div className="mt-3 flex items-start gap-2 rounded-xl border border-note-line bg-note-soft/80 px-3 py-2.5 text-xs text-note-ink">
                    <SparklesIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-note-ink" />
                    <span>
                      <strong>First BigQuery key</strong> — we'll also
                      provision your personal dataset{" "}
                      <span className="font-mono">{USER_RESOURCES.datasetId}</span>{" "}
                      and the{" "}
                      <span className="font-mono">{USER_RESOURCES.realtimeView}</span>{" "}
                      authorized view. Future BigQuery keys reuse them.
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end">
              <button
                onClick={handleCreate}
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-500 px-5 py-2.5 text-sm font-semibold text-on-brand shadow-brand-glow transition-all hover:bg-brand-600 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
              >
                <KeyIcon className="h-4 w-4" />
                Create key
              </button>
            </div>
          </div>
        </section>

        {/* Existing keys */}
        <section className="rounded-3xl border border-paper-300/70 bg-surface p-6 shadow-soft">
          <h2 className="font-display text-xl font-bold text-ink mb-5">
            Existing keys
          </h2>

          {keys.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-paper-300 bg-paper-100 py-10 text-center">
              <p className="text-sm text-ink-muted">
                No keys yet. Create one above to get started.
              </p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-2xl border border-paper-300/70">
              <table className="w-full">
                <thead className="bg-paper-100">
                  <tr>
                    <th className="px-5 py-3 text-left text-[11px] font-mono uppercase tracking-widest text-ink-faint">
                      Label
                    </th>
                    <th className="px-5 py-3 text-left text-[11px] font-mono uppercase tracking-widest text-ink-faint">
                      Permissions
                    </th>
                    <th className="px-5 py-3 text-left text-[11px] font-mono uppercase tracking-widest text-ink-faint">
                      Last used
                    </th>
                    <th className="px-5 py-3 text-right text-[11px] font-mono uppercase tracking-widest text-ink-faint">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((k) => (
                    <tr
                      key={k.id}
                      className="border-t border-paper-200 hover:bg-paper-100 transition-colors"
                    >
                      <td className="px-5 py-3">
                        <div className="font-mono text-sm text-ink">
                          {k.label}
                        </div>
                        <div className="text-[11px] text-ink-faint mt-0.5">
                          Created{" "}
                          {new Date(k.createdAt).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1">
                          {k.scopes.map((s) => (
                            <ScopePill key={s} scope={s} />
                          ))}
                        </div>
                      </td>
                      <td className="px-5 py-3 text-sm text-ink-muted">
                        {k.lastUsedAt
                          ? new Date(k.lastUsedAt).toLocaleString()
                          : "—"}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => setRevealed({ ...k, apiKey: undefined })}
                            title="View details"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-paper-200 hover:text-ink transition-colors"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setRotating(k)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-warn-line bg-warn-soft px-3 py-1.5 text-xs font-semibold text-warn-ink hover:border-warn transition-colors"
                          >
                            <SyncIcon className="h-3.5 w-3.5" />
                            Rotate
                          </button>
                          <button
                            onClick={() => setRevoking(k)}
                            title="Revoke key"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-muted hover:bg-bad-soft hover:text-bad-ink transition-colors"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      {/* Reveal modal */}
      {revealed && (
        <RevealModal
          mkey={revealed}
          onClose={() => setRevealed(null)}
        />
      )}

      {rotating && (
        <ConfirmDialog
          tone="amber"
          icon={<SyncIcon className="h-5 w-5" />}
          title={`Rotate "${rotating.label}"?`}
          body={
            <>
              <p>
                The current key string will stop working immediately. A new
                key string will be issued.
              </p>
              <p className="mt-2 text-ink-soft">
                Permissions, service account, and access to your data stay
                the same.
              </p>
            </>
          }
          confirmLabel="Rotate"
          onConfirm={handleRotateConfirm}
          onCancel={() => setRotating(null)}
        />
      )}

      {revoking && (
        <ConfirmDialog
          tone="red"
          icon={<TrashIcon className="h-5 w-5" />}
          title={`Revoke "${revoking.label}"?`}
          body={
            <>
              <p>
                The key will stop working immediately and the service account{" "}
                <span className="font-mono text-xs">
                  {revoking.serviceAccountEmail}
                </span>{" "}
                will lose access. Your dataset and bucket stay; other keys
                are unaffected.
              </p>
              <p className="mt-2 text-ink-soft">This can't be undone.</p>
            </>
          }
          confirmLabel="Revoke"
          onConfirm={handleRevokeConfirm}
          onCancel={() => setRevoking(null)}
        />
      )}
    </div>
  );
};

// ─── Reveal modal ───────────────────────────────────────────────────────

const RevealModal: React.FC<{ mkey: MockKey; onClose: () => void }> = ({
  mkey,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(!mkey.apiKey);
  const isReveal = !!mkey.apiKey;
  const hasBQ = mkey.scopes.some((s) => SCOPE_BY_ID[s].needsBigQuery);
  const hasStorage = mkey.scopes.includes("exports.read");

  const copy = () => {
    if (!mkey.apiKey) return;
    navigator.clipboard.writeText(mkey.apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-surface shadow-card-hover">
        <div className="border-b border-paper-300/70 px-6 py-5">
          <div className="flex items-start gap-3">
            <div
              className={`inline-flex h-10 w-10 items-center justify-center rounded-full flex-shrink-0 ${
                isReveal
                  ? "bg-warn-soft text-warn-ink"
                  : "bg-paper-200 text-ink-muted"
              }`}
            >
              {isReveal ? (
                <WarningIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </div>
            <div>
              {isReveal && (
                <p className="text-[11px] font-mono uppercase tracking-widest text-warn-ink">
                  One-time view
                </p>
              )}
              <h3 className="font-display text-xl font-bold text-ink mt-0.5">
                {isReveal ? "Save your key now" : `Key “${mkey.label}”`}
              </h3>
              <p className="text-sm text-ink-muted mt-1">
                {isReveal
                  ? "You won't see this key string again. Copy it now."
                  : "Details for this key."}
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-5 space-y-5">
          <ReadOnlyField label="Label" value={mkey.label} mono={false} />

          <div>
            <span className="block text-sm font-medium text-ink-soft mb-1.5">
              Permissions
            </span>
            <div className="flex flex-wrap gap-2 rounded-xl border border-paper-300 bg-paper-100 px-4 py-2.5">
              {mkey.scopes.map((s) => (
                <ScopePill key={s} scope={s} />
              ))}
            </div>
          </div>

          <ReadOnlyField
            label="Service account"
            value={mkey.serviceAccountEmail}
          />

          {/* Pointer to user-level resources. Not per-key. */}
          <div>
            <span className="block text-sm font-medium text-ink-soft mb-1.5">
              How to use this key
            </span>
            <div className="rounded-xl border border-paper-300 bg-paper-100 px-4 py-3 space-y-2 text-xs">
              {hasStorage && (
                <div className="flex items-start gap-2 text-ink-soft">
                  <DotIcon className="h-1.5 w-1.5 mt-1.5 flex-shrink-0 text-brand-500" />
                  <span>
                    <span className="text-ink-faint">Storage:</span> list /
                    download from{" "}
                    <span className="font-mono text-ink break-all">
                      {USER_RESOURCES.bucketPath}
                    </span>
                  </span>
                </div>
              )}
              {hasBQ && (
                <div className="flex items-start gap-2 text-ink-soft">
                  <DotIcon className="h-1.5 w-1.5 mt-1.5 flex-shrink-0 text-brand-500" />
                  <span>
                    <span className="text-ink-faint">BigQuery:</span> query{" "}
                    <span className="font-mono text-ink break-all">
                      {USER_RESOURCES.datasetId}.{USER_RESOURCES.realtimeView}
                    </span>
                  </span>
                </div>
              )}
              <p className="text-[11px] text-ink-faint pt-1">
                These resources belong to your account and are shared by all
                your keys.
              </p>
            </div>
          </div>

          {isReveal && (
            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1.5">
                API key string
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  type="text"
                  value={mkey.apiKey}
                  readOnly
                  className="flex-grow rounded-xl border border-paper-300 bg-paper-100 px-4 py-2.5 font-mono text-xs text-ink"
                />
                <button
                  onClick={copy}
                  className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${
                    copied
                      ? "bg-brand-500 text-on-brand"
                      : "bg-ink text-surface hover:bg-ink-soft hover:-translate-y-0.5"
                  }`}
                >
                  {copied ? (
                    <>
                      <CheckIcon className="h-4 w-4" />
                      Copied
                    </>
                  ) : (
                    <>
                      <CopyIcon className="h-4 w-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <button className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-paper-300 bg-surface px-4 py-2.5 text-sm font-semibold text-ink-muted hover:bg-paper-100 hover:text-ink transition-colors">
                <DownloadIcon className="h-4 w-4" />
                Download as file
              </button>
              <label className="mt-3 flex items-start gap-3 rounded-2xl border border-paper-300 bg-paper-100 p-4 cursor-pointer hover:bg-paper-200/70 transition-colors">
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-paper-400 text-brand-500 focus:ring-brand-500"
                />
                <span className="text-sm text-ink-soft">
                  I've saved this key somewhere safe.
                </span>
              </label>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-paper-300/70 bg-surface px-6 py-4">
          <button
            onClick={onClose}
            disabled={!confirmed}
            className={`inline-flex items-center rounded-full px-5 py-2 text-sm font-semibold transition-all ${
              confirmed
                ? "bg-brand-500 text-on-brand hover:bg-brand-600 hover:-translate-y-0.5"
                : "bg-paper-200 text-ink-faint cursor-not-allowed"
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ReadOnlyField: React.FC<{
  label: string;
  value: string;
  mono?: boolean;
}> = ({ label, value, mono = true }) => (
  <div>
    <label className="block text-sm font-medium text-ink-soft mb-1.5">
      {label}
    </label>
    <input
      type="text"
      value={value}
      readOnly
      className={`w-full rounded-xl border border-paper-300 bg-paper-100 px-4 py-2.5 text-xs text-ink ${
        mono ? "font-mono" : "text-sm"
      }`}
    />
  </div>
);

// ─── Confirm dialog ─────────────────────────────────────────────────────

const ConfirmDialog: React.FC<{
  tone: "amber" | "red";
  icon: React.ReactNode;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ tone, icon, title, body, confirmLabel, onConfirm, onCancel }) => {
  const palette =
    tone === "red"
      ? {
          icon: "bg-bad-soft text-bad-ink",
          button: "bg-bad text-on-brand hover:bg-bad/90",
        }
      : {
          icon: "bg-warn-soft text-warn-ink",
          button: "bg-warn text-on-brand hover:bg-warn/90",
        };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/40 p-4">
      <div className="w-full max-w-md rounded-3xl bg-surface shadow-card-hover">
        <div className="px-6 py-5">
          <div className="flex items-start gap-3 mb-3">
            <div
              className={`inline-flex h-10 w-10 items-center justify-center rounded-full flex-shrink-0 ${palette.icon}`}
            >
              {icon}
            </div>
            <div>
              <h3 className="font-display text-lg font-bold text-ink">
                {title}
              </h3>
            </div>
          </div>
          <div className="text-sm text-ink-muted space-y-2 pl-13">{body}</div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-paper-300/70 bg-paper-100/50 px-6 py-4">
          <button
            onClick={onCancel}
            className="inline-flex items-center rounded-full border border-paper-300 bg-surface px-4 py-2 text-sm font-semibold text-ink-muted hover:bg-paper-100 hover:text-ink transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`inline-flex items-center rounded-full px-5 py-2 text-sm font-semibold transition-all hover:-translate-y-0.5 ${palette.button}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApiKeysPreviewPage;
